<template>
  <section
    id="join-us"
    class="careers-module-component module">
    <div class="container large">
      <div
        v-if="data.careers_headline"
        class="headline h1 font-med"
        v-html="data.careers_headline"></div>
      <div class="careers-wrapper">
        <div class="filters">
          <DropdownFilter
            v-model="selected[1]"
            :parent-selected="selected[1]"
            :options="locations"
            :default-val="defaultValue[1]"
            theme="orange"
            trigger-text="Locations" />
          <DropdownFilter
            v-model="selected[0]"
            :parent-selected="selected[0]"
            :options="categories"
            :default-val="defaultValue[0]"
            theme="blue"
            trigger-text="Departments" />
        </div>
        <div class="jobs-section">
          <template v-if="openPositions.length > 0">
            <ul class="jobs-container jobs-wrapper">
              <li
                v-for="(job, idx) in allJobs
                  ? openPositions
                  : openPositions.slice(0, 8)"
                :key="`job-${idx}`"
                class="job-wrapper">
                <a
                  class="job"
                  :href="job.absolute_url"
                  target="_blank">
                  <div class="job-top">
                    <span class="category font-uppercase">{{
                      job.metadata?.find(
                        (m) => m.name === 'Career Site Category'
                      )?.value?.[0] || ''
                    }}</span>
                    <div class="locations">
                      <p
                        v-for="(location, index) in job.metadata?.find(
                          (m) => m.name === 'Career Site Location'
                        )?.value || []"
                        :key="index">
                        {{ location }}
                      </p>
                    </div>
                  </div>
                  <span class="job-title font-larger">{{ job.title }}</span>
                  <button class="btn">View Position</button>
                </a>
              </li>
            </ul>
          </template>
          <div
            v-else
            class="jobs-container no-jobs">
            <span
              v-if="
                selected[0][0] === defaultValue[0] &&
                selected[1][0] === defaultValue[1]
              "
              >There are no open positions. Please check back later.</span
            >
            <span v-else
              >Sorry, we couldn't find any job postings that match your selected
              criteria</span
            >
          </div>
          <div
            v-if="openPositions.length > 8 && !allJobs"
            class="view-more"
            @click="showAllJobs">
            <p class="font-med">View More</p>
          </div>
        </div>

        <div
          v-if="talentCommunities.length > 0"
          class="jobs-section">
          <h3 class="section-subheading">Talent Communities</h3>
          <ul class="jobs-container jobs-wrapper">
            <li
              v-for="(job, idx) in talentCommunities"
              :key="`tc-${idx}`"
              class="job-wrapper">
              <a
                class="job"
                :href="job.absolute_url"
                target="_blank">
                <div class="job-top">
                  <span class="category font-uppercase">{{
                    job.metadata?.find((m) => m.name === 'Career Site Category')
                      ?.value?.[0] || ''
                  }}</span>
                  <div class="locations">
                    <p
                      v-for="(location, index) in job.metadata?.[1]?.value ||
                      []"
                      :key="index">
                      {{ location }}
                    </p>
                  </div>
                </div>
                <span class="job-title font-larger">{{ job.title }}</span>
                <button class="btn">View Position</button>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* #META
   {
    "NAME": "CareersComponent",
    "CLASSNAME": "careers-module-component",
    "URL": "http://eikon.local/"
   }
  #ENDMETA */

import { computed, onMounted, ref, watch } from 'vue'
import DropdownFilter from '../utility/DropdownFilter.vue'
defineProps(['data'])

const defaultValue = ['All Departments', 'All Locations']
const categories = ref([])
const locations = ref([])
const jobs = ref([])
const selected = ref([[defaultValue[0]], [defaultValue[1]]])

const allJobs = ref(false)

const showAllJobs = () => {
  allJobs.value = true
}

const organizeJobs = (rawJobs) => {
  let categoriesCopy = [...categories.value]
  categoriesCopy.shift()
  jobs.value = categoriesCopy.map((category) => {
    return {
      category: category.name,
      jobs: [],
    }
  })

  // Add a fallback category for uncategorized jobs
  jobs.value.push({
    category: 'Other',
    jobs: [],
  })

  rawJobs.forEach((job) => {
    // Get the job's category name safely
    const jobCategory = job.metadata?.find(
      (m) => m.name === 'Career Site Category'
    )?.value?.[0]
    // Try to find a matching category
    const categoryObj = jobs.value.find((cat) => cat.category === jobCategory)
    if (categoryObj && jobCategory) {
      categoryObj.jobs.push(job)
    } else {
      // If no category found or category is missing, add to 'Other'
      jobs.value[jobs.value.length - 1].jobs.push(job)
    }
  })
}

const filteredByCategory = computed(() => {
  let filtered = jobs.value

  if (selected.value[0][0] !== defaultValue[0]) {
    filtered = filtered.filter((job) => {
      return selected.value[0].includes(job.category)
    })
  }

  if (selected.value[1][0] !== defaultValue[1]) {
    filtered = filtered.map((job) => {
      return {
        ...job,
        jobs: job.jobs.filter((jobDetail) => {
          return (
            jobDetail.metadata
              ?.find((m) => m.name === 'Career Site Location')
              ?.value?.some((e) => selected.value[1].includes(e)) || false
          )
        }),
      }
    })
  }

  return filtered
})

const openPositions = computed(() => {
  return filteredByCategory.value
    .filter((el) => el.category !== 'Other')
    .map((el) => el.jobs)
    .flat()
})

const talentCommunities = computed(() => {
  const other = jobs.value.find((el) => el.category === 'Other')
  return other ? other.jobs : []
})

const rawJobs = ref([])

watch(rawJobs, (val) => organizeJobs(val))

onMounted(async () => {
  const res = await fetch(
    'https://boards-api.greenhouse.io/v1/boards/eikontherapeutics/jobs'
  )
  const data = await res.json()
  const fetchedJobs = data.jobs || []

  const locationMap = {}
  const categoryMap = {}

  fetchedJobs.forEach((job) => {
    job.metadata?.forEach((field) => {
      if (field.name === 'Career Site Location' && Array.isArray(field.value)) {
        field.value.forEach((val) => {
          locationMap[val] = { name: val }
        })
      } else if (
        field.name === 'Career Site Category' &&
        Array.isArray(field.value)
      ) {
        field.value.forEach((val) => {
          categoryMap[val] = { name: val }
        })
      }
    })
  })

  const sortedLocations = Object.keys(locationMap)
    .sort()
    .map((k) => locationMap[k])
  const sortedCategories = Object.keys(categoryMap)
    .sort()
    .map((k) => categoryMap[k])

  locations.value = [{ name: defaultValue[1] }, ...sortedLocations]
  categories.value = [{ name: defaultValue[0] }, ...sortedCategories]

  rawJobs.value = fetchedJobs
})
</script>

<style lang="scss">
.careers-module-component {
  @apply relative w-full  py-8 lg:py-10 px-6
  min-h-[320px];

  .headline {
    margin: 0 0 60px;

    @include mobile {
      margin: 0 0 40px;
    }
  }

  .careers-wrapper {
    position: relative;
    padding: 100px 0 0;
  }

  .filters {
    position: absolute;
    top: 0;
    @apply z-5
    w-full flex flex-col gap-[7px] mb-[40px]
    lg:px-0 lg:flex-row lg:justify-center lg:gap-[12px];
  }
  .jobs-section {
    & + .jobs-section {
      margin-top: 60px;
    }
  }

  .section-subheading {
    font-size: 2rem;
    letter-spacing: 1.44px;
    text-transform: uppercase;
    margin: 0 0 24px;
    @include font-main-med;
  }

  .jobs-container {
    &.jobs-wrapper {
      display: flex;
      flex-wrap: wrap;

      .job-wrapper {
        width: 24%;
        margin-right: 1.33%;
        margin-bottom: 1.33%;

        &:nth-child(4n + 4) {
          margin-right: 0;
        }

        @media (max-width: 1200px) {
          width: 32.5%;
          margin-right: 1.25%;
          margin-bottom: 1.25%;

          &:nth-child(4n + 4) {
            margin-right: 1.25%;
          }

          &:nth-child(3n + 3) {
            margin-right: 0;
          }
        }

        @include tablet-landscape(1) {
          width: 48.5%;
          margin-right: 3%;
          margin-bottom: 3%;

          &:nth-child(3n + 3) {
            margin-right: 3%;
          }

          &:nth-child(2n + 2) {
            margin-right: 0;
          }
        }

        @include mobile {
          width: 100%;
          margin-right: 0;
          margin-bottom: 20px;

          &:nth-child(2n + 2) {
            margin-right: 0;
          }
        }

        .job {
          @apply flex flex-col items-center gap-[30px]
           p-[30px] rounded-[20px] w-full
           lg:gap-[40px];
          height: 100%;
          background: $light-gray;

          &-top {
            @apply flex flex-col items-center gap-[5px] 
            text-[16px];
            text-align: center;

            .category {
              @apply mb-[10px];
            }
          }

          &-title {
            @apply flex justify-center items-center h-[80px] max-w-[320px] text-center;
          }

          .btn {
            margin: auto 0 0;
          }
        }
      }
    }
    &.no-jobs {
      @apply flex justify-center;
      span {
        @apply leading-[1.2] font-medium text-5xl;
      }
    }
  }

  .view-more {
    cursor: pointer;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 24px 0 0;
    color: $indigo;

    &:before {
      content: '';
      display: block;
      width: 17.23px;
      height: 16px;
      margin-right: 8px;
      background-image: url('@/assets/img/dotted-arrow-down.svg');
      background-size: contain;
      background-repeat: no-repeat;
    }
  }
}
</style>
